import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, Renderer2, SecurityContext, inject, viewChild } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { environment } from "@env/environment";
import { ThemeService } from "@app/core/services/theme.service";
import { I18nService } from "@app/core/services/i18n.service";
import { ROUTES } from "@app/shared/constants/routes.constants";
import { SystemSettingsService } from "@app/core/services/system-settings.service";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { ServiceWorkerService } from "@app/core/services/service-worker.service";
import { MessageService } from "@app/core/services/message.service";
import { DomSanitizer, Title } from "@angular/platform-browser";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { LoaderFullscreenComponent } from "@app/shared/components/loader/loader-fullscreen/loader-fullscreen.component";
import { BreakpointService } from "@app/core/services/breakpoint.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TimeoutService } from "@app/core/services/timeout.service";
import { registerLocaleData } from "@angular/common";
import localeESUS from "@angular/common/locales/es-US";
import localeEN from "@angular/common/locales/en";
import { LANGUAGES } from "@app/shared/constants/localization.constants";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [LoaderFullscreenComponent, RouterOutlet]
})
export class AppComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private i18nService = inject(I18nService);
  private systemSettingsService = inject(SystemSettingsService);
  private authService = inject(AuthenticationService);
  private swService = inject(ServiceWorkerService);
  private breakpointService = inject(BreakpointService);
  private domSanitizer = inject(DomSanitizer);
  private renderer = inject(Renderer2);
  private messageService = inject(MessageService);
  private titleService = inject(Title);
  private userSettingsService = inject(UserSettingsService);
  private timeoutService = inject(TimeoutService);
  private _mainDiv = viewChild<ElementRef>("mainDiv");
  private _destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    registerLocaleData(localeEN, LANGUAGES.ENGLISH.value);
    registerLocaleData(localeESUS, LANGUAGES.SPANISH.value);
  }

  ngOnInit() {
    this._startBreakPointInterceptor();
    this._startMessageInterceptor();
    this._loadSystemSettings();
  }

  ngAfterViewInit() {
    this._loadBackground();
  }

  get companyLargeLogo(): string {
    return this.systemSettingsService.largeLogo;
  }

  get background(): string {
    return this.systemSettingsService?.backgroundImage;
  }

  /**
   * Only show additional loader if background image is taking a long time to load and user is not logged in.
   */
  get showLoader(): boolean {
    return !this.background && !this.authService.isAuthenticated() && !this.router.url.includes(ROUTES.ERROR);
  }

  /**
   * Manage mobile vs desktop break point states
   */
  private _startBreakPointInterceptor() {
    this.breakpointService.startBreakPointInterceptor();
  }

  /**
   * Load / configure system settings
   *
   * NOTE: System setting were fetched during app initialization,
   *       now we need to apply these settings to the application.
   *
   *       If the system settings have loaded properly then continue...
   *       Otherwise... route user to not-available-error module.
   */
  private _loadSystemSettings() {
    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    // If the system settings have loaded properly then continue...
    // Otherwise... route user to not-available-error module.
    const systemSettings = this.systemSettingsService.systemSettings;

    if (!systemSettings) {
      this.router.navigate([ROUTES.ERROR, ROUTES.NOT_AVAILABLE]);
      return;
    }

    this.titleService.setTitle(systemSettings.name);
    this.themeService.setThemeAttributes(this.userSettingsService.theme ?? systemSettings.themeSettings, true);
    this.i18nService.language = this.userSettingsService?.language ?? systemSettings.localeSettings.language;
    this.timeoutService.timeOutSettings = systemSettings.timeoutSettings;
    this.swService.checkForUpdates();
    // Need cookies to be enabled in order to use the site
    if (!navigator.cookieEnabled || !localStorage) {
      this.router.navigate([ROUTES.ERROR]);
    } else {
      this.router.navigate([this.authService.isAuthenticated() ? ROUTES.HOME : ROUTES.LOGIN]);
    }
  }

  /**
   * Monitor when domain images have been updated
   */
  private _startMessageInterceptor() {
    this.messageService
      .getBackgroundMessage()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._loadBackground();
      });
  }

  /**
   * Load background image
   */
  private _loadBackground() {
    try {
      const _mainDiv = this._mainDiv();
      if (_mainDiv) {
        this.renderer.setStyle(
          _mainDiv.nativeElement,
          "background",
          this.domSanitizer.sanitize(SecurityContext.STYLE, "url(" + this.background + ") no-repeat center center/cover fixed")
        );
      }
    } catch {}
  }
}
